import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, MagnifyingGlass, ArrowsClockwise, Eye, PencilSimple,
         CheckCircle, ArrowCounterClockwise, Trash, ClipboardText, CurrencyDollar,
         ListChecks, Gear, UserPlus, Download } from "@phosphor-icons/react";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api from "../lib/api";
import { formatDate } from "../lib/utils-extra";

const ACTION_META = {
  evaluation_created:        { icon: ClipboardText, color: "#0EA5E9", label: "Evaluation Created" },
  evaluation_edited:         { icon: PencilSimple,  color: "#0284C7", label: "Evaluation Edited" },
  evaluation_finalized:      { icon: CheckCircle,   color: "#16A34A", label: "Evaluation Finalized" },
  evaluation_viewed:         { icon: Eye,           color: "#475569", label: "Evaluation Viewed" },
  evaluation_reopened:       { icon: ArrowCounterClockwise, color: "#D97706", label: "Evaluation Reopened" },
  evaluation_deleted:        { icon: Trash,         color: "#DC2626", label: "Evaluation Deleted" },
  evaluation_acknowledged:   { icon: CheckCircle,   color: "#16A34A", label: "Evaluation Acknowledged" },
  evaluation_pdf_downloaded: { icon: Download,      color: "#0284C7", label: "PDF Downloaded" },
  bonus_cycle_created:       { icon: CurrencyDollar, color: "#0EA5E9", label: "Bonus Cycle Created" },
  bonus_cycle_calculated:    { icon: CurrencyDollar, color: "#0284C7", label: "Bonus Calculated" },
  bonus_cycle_finalized:     { icon: CurrencyDollar, color: "#16A34A", label: "Bonus Finalized" },
  bonus_cycle_deleted:       { icon: Trash,         color: "#DC2626", label: "Bonus Cycle Deleted" },
  kpi_template_created:      { icon: ListChecks,    color: "#0EA5E9", label: "KPI Template Created" },
  kpi_template_updated:      { icon: ListChecks,    color: "#0284C7", label: "KPI Template Updated" },
  kpi_template_deleted:      { icon: Trash,         color: "#DC2626", label: "KPI Template Deleted" },
  settings_updated:          { icon: Gear,          color: "#475569", label: "Settings Updated" },
  user_registered:           { icon: UserPlus,      color: "#0EA5E9", label: "User Registered" },
};

const ROLE_COLOR = {
  owner:   "bg-[#0EA5E9]/10 text-[#0EA5E9]",
  hr:      "bg-[#16A34A]/10 text-[#16A34A]",
  manager: "bg-[#D97706]/10 text-[#D97706]",
  employee:"bg-[#F1F5F9] text-[#475569]",
};

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (actionFilter !== "all") params.action = actionFilter;
      if (roleFilter !== "all") params.role = roleFilter;
      const { data } = await api.get("/audit-logs", { params: { ...params, limit: 500 } });
      setLogs(data);
    } finally { setLoading(false); }
  }, [actionFilter, roleFilter]);

  useEffect(() => { reload(); }, [reload]);

  const filtered = logs.filter((l) => {
    if (!search) return true;
    const hay = `${l.user_name} ${l.user_email} ${l.record_label} ${l.record_id} ${l.ip_address}`.toLowerCase();
    return hay.includes(search.toLowerCase());
  });

  const formatTs = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="small-label">Compliance</div>
          <h1 className="font-display text-3xl font-semibold mt-1 flex items-center gap-2">
            <ShieldCheck size={28} weight="duotone" color="#0EA5E9" /> Audit Log
          </h1>
          <p className="text-[#475569] text-sm mt-1">Tamper-evident trail of every sensitive action.</p>
        </div>
        <Button variant="outline" onClick={reload} disabled={loading} data-testid="audit-refresh-button">
          <ArrowsClockwise size={16} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <Card className="surface-card border-[#E2E8F0]">
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
            <Input
              className="pl-9 h-10"
              placeholder="Search by name, email, record, IP…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="audit-search-input"
            />
          </div>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="h-10" data-testid="audit-action-filter"><SelectValue placeholder="All actions" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {Object.entries(ACTION_META).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="h-10" data-testid="audit-role-filter"><SelectValue placeholder="All roles" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="owner">Owner</SelectItem>
              <SelectItem value="hr">HR / Admin</SelectItem>
              <SelectItem value="manager">Manager</SelectItem>
              <SelectItem value="employee">Employee</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="surface-card border-[#E2E8F0]">
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#475569]">
              {loading ? "Loading…" : "No audit entries match your filters."}
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-[#475569] uppercase tracking-wider border-b border-[#E2E8F0]">
                      <th className="px-4 py-3">When</th>
                      <th className="px-4 py-3">Actor</th>
                      <th className="px-4 py-3">Role</th>
                      <th className="px-4 py-3">Action</th>
                      <th className="px-4 py-3">Record</th>
                      <th className="px-4 py-3">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((l) => {
                      const meta = ACTION_META[l.action] || { icon: ShieldCheck, color: "#475569", label: l.action };
                      const Icon = meta.icon;
                      return (
                        <tr key={l.id} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC]"
                            data-testid={`audit-row-${l.id}`}>
                          <td className="px-4 py-3 font-mono-data text-xs">{formatTs(l.timestamp)}</td>
                          <td className="px-4 py-3">
                            <div className="font-medium">{l.user_name || "—"}</div>
                            <div className="text-xs text-[#475569]">{l.user_email}</div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={`${ROLE_COLOR[l.role] || ROLE_COLOR.employee} border-0 capitalize`}>
                              {l.role || "—"}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Icon size={16} weight="duotone" color={meta.color} />
                              <span className="font-medium">{meta.label}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm">{l.record_label || "—"}</div>
                            <div className="text-[11px] text-[#475569] font-mono-data">{l.record_type} · {l.record_id?.slice(0, 8)}</div>
                          </td>
                          <td className="px-4 py-3 font-mono-data text-xs text-[#475569]">{l.ip_address || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-[#E2E8F0]">
                {filtered.map((l) => {
                  const meta = ACTION_META[l.action] || { icon: ShieldCheck, color: "#475569", label: l.action };
                  const Icon = meta.icon;
                  return (
                    <div key={l.id} className="p-4" data-testid={`audit-mobile-${l.id}`}>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                             style={{ background: `${meta.color}15` }}>
                          <Icon size={18} weight="duotone" color={meta.color} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium">{meta.label}</div>
                          <div className="text-xs text-[#475569] mt-0.5">{l.record_label || l.record_type}</div>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className={`${ROLE_COLOR[l.role] || ROLE_COLOR.employee} border-0 capitalize text-[10px]`}>
                              {l.role || "—"}
                            </Badge>
                            <span className="text-xs text-[#475569]">{l.user_name}</span>
                          </div>
                          <div className="text-[10px] text-[#475569] mt-1 font-mono-data">{formatTs(l.timestamp)} · {l.ip_address || "—"}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <div className="text-xs text-[#475569]">
        Showing <strong>{filtered.length}</strong> of {logs.length} entries · most recent first.
      </div>
    </div>
  );
}
